/**
 * Vym — Klaviyo pop-up sequencing control
 * ----------------------------------------
 * Two on-site pop-ups exist:
 *   - "10% off" discount pop-up (QYWDL7), timed by its own Klaviyo settings
 *     (10-second delay). This script never opens it.
 *   - "Build your bundle" quiz pop-up (SZsPYW), on a manual trigger, opened
 *     only by this script.
 *
 * 31 Oct 2026 fix: a customer reported the 10% pop-up appearing while they
 * were taking the quiz. Two causes:
 *   1. The discount pop-up fires 10 seconds after any page load, including
 *      the quiz page and while the quiz modal is open.
 *   2. The quiz page was renamed /pages/build-your-stack (short link /stack),
 *      but the exclusion list still only named /pages/build-your-bundle, so the
 *      quiz invite pop-up could also open on top of the quiz page itself.
 * Fix: on the quiz page, and from the moment the quiz modal is opened, the
 * html element gets the class "vym-quiz-active", which hides every Klaviyo
 * pop-up for the rest of that page view. The quiz page paths are added to the
 * exclusions and count as "quiz started".
 *
 * Sequencing rules (unchanged):
 *   1. Discount pop-up dismissed -> quiz pop-up 30 s later.
 *   2. Discount pop-up submitted -> no quiz pop-up this page view.
 *   3. Discount pop-up doesn't open within 20 s -> quiz pop-up after 45 s.
 * Exclusions for the quiz pop-up: existing customers, quiz pages, /cart,
 * /checkout, 7-day cool-off, and anyone who has already started the quiz.
 */
(function () {
  "use strict";

  var QUIZ_FORM_ID = "SZsPYW";
  var DISCOUNT_FORM_ID = "QYWDL7";

  var DISCOUNT_WAIT_MS = 20000;
  var QUIZ_AFTER_DISMISS_DELAY_MS = 30000;
  var QUIZ_FALLBACK_DELAY_MS = 45000;

  var QUIZ_STARTED_KEY = "vymQuizStarted";
  var POPUP_SEEN_KEY = "vymPopupSeenAt";
  var COOL_OFF_MS = 7 * 24 * 60 * 60 * 1000;
  var QUIZ_PAGE_PATHS = ["/pages/build-your-stack", "/pages/build-your-bundle", "/stack"];
  var EXCLUDED_PATHS = QUIZ_PAGE_PATHS.concat(["/cart", "/checkout"]);

  // Hide Klaviyo pop-ups (both forms render as role="dialog" aria-label="POPUP Form",
  // wrapped in a plain div directly under <body>) while the quiz is in use.
  (function injectStyle() {
    var css =
      'html.vym-quiz-active body > div:has(div[role="dialog"][aria-label="POPUP Form"]),' +
      'html.vym-quiz-active div[role="dialog"][aria-label="POPUP Form"]' +
      "{display:none !important;}";
    var style = document.createElement("style");
    style.id = "vym-quiz-popup-guard";
    style.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(style);
  })();

  function readLocalStorage(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function writeLocalStorage(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) {}
  }
  function hasStartedQuiz() { return readLocalStorage(QUIZ_STARTED_KEY) === "1"; }
  function markQuizStarted() { writeLocalStorage(QUIZ_STARTED_KEY, "1"); }
  function markPopupSeen() { writeLocalStorage(POPUP_SEEN_KEY, String(Date.now())); }
  function inCoolOff() {
    var seenAt = parseInt(readLocalStorage(POPUP_SEEN_KEY), 10);
    if (!seenAt || isNaN(seenAt)) return false;
    return Date.now() - seenAt < COOL_OFF_MS;
  }
  function isExistingCustomer() { return window.vymIsExistingCustomer === true; }

  function currentPath() {
    return window.location.pathname.toLowerCase().replace(/\/$/, "");
  }
  function pathMatches(list) {
    var path = currentPath();
    for (var i = 0; i < list.length; i++) {
      if (path === list[i] || path.indexOf(list[i] + "/") === 0 || (list[i].length > 6 && path.indexOf(list[i]) !== -1)) return true;
    }
    return false;
  }
  function isQuizPage() { return pathMatches(QUIZ_PAGE_PATHS); }
  function isExcludedPath() { return pathMatches(EXCLUDED_PATHS); }

  function setQuizActive() {
    document.documentElement.classList.add("vym-quiz-active");
  }

  function openKlaviyoForm(formId) {
    if (document.documentElement.classList.contains("vym-quiz-active")) return;
    if (window.klaviyo && typeof window.klaviyo.openForm === "function") {
      window.klaviyo.openForm(formId);
      return;
    }
    window.klaviyo = window.klaviyo || [];
    if (typeof window.klaviyo.push === "function") {
      window.klaviyo.push(["openForm", formId]);
    }
  }

  var quizTimerId = null;
  function clearQuizTimer() {
    if (quizTimerId !== null) { window.clearTimeout(quizTimerId); quizTimerId = null; }
  }
  function scheduleQuiz(delayMs) {
    clearQuizTimer();
    quizTimerId = window.setTimeout(function () {
      quizTimerId = null;
      openKlaviyoForm(QUIZ_FORM_ID);
    }, delayMs);
  }

  // The quiz page itself: hide pop-ups for the whole visit and count it as a quiz start.
  if (isQuizPage()) {
    setQuizActive();
    markQuizStarted();
  }

  // Watch the quiz modal on every page, for every visitor (including returning ones).
  function onQuizModalOpened() {
    clearQuizTimer();
    markQuizStarted();
    setQuizActive();
  }
  function watchQuizModal() {
    var overlay = document.getElementById("vymqmOverlay");
    if (!overlay) { window.setTimeout(watchQuizModal, 300); return; }
    if (overlay.classList.contains("is-open")) { onQuizModalOpened(); return; }
    var observer = new MutationObserver(function () {
      if (overlay.classList.contains("is-open")) { onQuizModalOpened(); observer.disconnect(); }
    });
    observer.observe(overlay, { attributes: true, attributeFilter: ["class"] });
  }
  watchQuizModal();

  window.addEventListener("klaviyoForms", function (event) {
    var detail = event && event.detail;
    if (!detail) return;
    if (detail.formId === QUIZ_FORM_ID) {
      if (detail.type === "open" || detail.type === "close") markPopupSeen();
      return;
    }
    if (detail.formId === DISCOUNT_FORM_ID) {
      if (detail.type === "open" || detail.type === "submit") {
        clearQuizTimer();
      } else if (detail.type === "close") {
        if (!hasStartedQuiz() && !isExcludedPath() && !inCoolOff() && !isExistingCustomer()) {
          scheduleQuiz(QUIZ_AFTER_DISMISS_DELAY_MS);
        }
      }
    }
  });

  if (isExistingCustomer() || hasStartedQuiz() || isExcludedPath() || inCoolOff()) {
    return;
  }

  scheduleQuiz(QUIZ_FALLBACK_DELAY_MS);
})();
